import React, { useState, useEffect } from 'react'
import {
  ArrowLeft,
  Save,
  RotateCcw
} from 'lucide-react'
import Breadcrumb from '../../components/Common/Breadcrumb'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import assetDisposalService from '../../services/assetDisposalService'
import assetAcquisitionService from '../../services/assetAcquisitionService'
import assetDropdownService, { ASSET_DROPDOWN_FALLBACKS, optionsFromDropdown } from '../../services/assetDropdownService'
import { toDateInputValue } from './assetUtils'
import { labelStyle, inputStyle, textareaStyle, gridStyle, sectionStyle, fullWidthStyle, requiredStyle } from './assetFormStyles'

const emptyForm = {
  disposalCode: '',
  acquisition: '',
  disposalDate: '',
  disposalReason: 'Other',
  disposalValue: 0,
  currency: 'INR',
  status: 'Draft',
  notes: ''
}

const AssetDisposalForm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const isEditMode = location.pathname.includes('/edit')
  const isViewMode = location.pathname.includes('/view')
  const disposalData = location.state?.disposalData
  const recordId = searchParams.get('id') || disposalData?._id

  const [formData, setFormData] = useState(emptyForm)
  const [acquisitions, setAcquisitions] = useState([])
  const [reasonOptions, setReasonOptions] = useState(optionsFromDropdown(null, 'disposalReason', ASSET_DROPDOWN_FALLBACKS.disposalReason))
  const [statusOptions, setStatusOptions] = useState(optionsFromDropdown(null, 'disposalStatus', ASSET_DROPDOWN_FALLBACKS.disposalStatus))
  const [currencyOptions, setCurrencyOptions] = useState(optionsFromDropdown(null, 'currency', ASSET_DROPDOWN_FALLBACKS.currency))
  const [isLoadingCode, setIsLoadingCode] = useState(true)
  const [isLoading, setIsLoading] = useState(false)

  // Fetch next disposal code (only for new records)
  useEffect(() => {
    if (!isEditMode && !isViewMode) {
      const fetchCode = async () => {
        try {
          setIsLoadingCode(true)
          const response = await assetDisposalService.getNextDisposalCode()
          if (response.success && response.data) {
            setFormData(prev => ({ ...prev, disposalCode: response.data.disposalCode || 'ADSP0001' }))
          }
        } catch (error) {
          console.error('Error fetching disposal code:', error)
          setFormData(prev => ({ ...prev, disposalCode: 'ADSP0001' }))
        } finally {
          setIsLoadingCode(false)
        }
      }
      fetchCode()
    } else {
      setIsLoadingCode(false)
    }
  }, [isEditMode, isViewMode])

  useEffect(() => {
    loadAcquisitions()
  }, [])

  useEffect(() => {
    if ((isEditMode || isViewMode) && recordId) {
      loadDisposalData(recordId)
    }
  }, [isEditMode, isViewMode, recordId])

  const loadAcquisitions = async () => {
    try {
      const [acqResponse, dropdownResponse] = await Promise.all([
        assetAcquisitionService.getAllAcquisitions({ limit: 1000 }),
        assetDropdownService.getOptions()
      ])
      if (acqResponse.success) setAcquisitions(acqResponse.data || [])
      if (dropdownResponse.success && dropdownResponse.data) {
        setReasonOptions(optionsFromDropdown(dropdownResponse.data.dropdowns, 'disposalReason', ASSET_DROPDOWN_FALLBACKS.disposalReason))
        setStatusOptions(optionsFromDropdown(dropdownResponse.data.dropdowns, 'disposalStatus', ASSET_DROPDOWN_FALLBACKS.disposalStatus))
        setCurrencyOptions(optionsFromDropdown(dropdownResponse.data.dropdowns, 'currency', ASSET_DROPDOWN_FALLBACKS.currency))
      }
    } catch (error) {
      console.error('Error loading acquisitions:', error)
      if (!error._errorHandled) toast.error('Failed to load dropdown options')
    }
  }

  const loadDisposalData = async (id) => {
    try {
      setIsLoading(true)
      const response = await assetDisposalService.getDisposalById(id)
      if (response.success && response.data) {
        const record = response.data
        setFormData({
          disposalCode: record.disposalCode || '',
          acquisition: record.acquisition?._id || record.acquisition || '',
          disposalDate: toDateInputValue(record.disposalDate),
          disposalReason: record.disposalReason || 'Other',
          disposalValue: record.disposalValue || 0,
          currency: record.currency || 'INR',
          status: record.status || 'Draft',
          notes: record.notes || ''
        })
      }
    } catch (error) {
      console.error('Error loading disposal data:', error)
      toast.error('Failed to load disposal data')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }))
  }

  const handleSave = async () => {
    try {
      if (!formData.acquisition) {
        toast.error('Please select the asset being disposed')
        return
      }

      setIsLoading(true)

      const submitData = {
        ...formData,
        acquisition: formData.acquisition || null,
        disposalDate: formData.disposalDate || null
      }

      if (isEditMode && recordId) {
        const response = await assetDisposalService.updateDisposal(recordId, submitData)
        if (response.success) {
          toast.success('Disposal updated successfully!')
          navigate('/assets/disposals')
        } else {
          toast.error(response.message || 'Failed to update disposal')
        }
      } else {
        const response = await assetDisposalService.createDisposal(submitData)
        if (response.success) {
          toast.success('Disposal created successfully!')
          navigate('/assets/disposals')
        } else {
          toast.error(response.message || 'Failed to create disposal')
        }
      }
    } catch (error) {
      console.error('Error saving disposal:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else if (!error._errorHandled) {
        toast.error('Failed to save disposal')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleReset = () => {
    if ((isEditMode || isViewMode) && recordId) {
      loadDisposalData(recordId)
    } else {
      setFormData({ ...emptyForm, disposalCode: formData.disposalCode })
    }
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Assets', path: '/assets' },
          { label: 'Disposals', path: '/assets/disposals' },
          { label: isEditMode ? 'Edit' : isViewMode ? 'View' : 'New', isCurrent: true }
        ]}
      />

      <div className="page-header">
        <div className="page-title">
          <h1>{isEditMode ? 'Edit Disposal' : isViewMode ? 'View Disposal' : 'New Disposal'}</h1>
          <p>{isViewMode ? 'View disposal details' : isEditMode ? 'Update disposal information' : 'Record an asset disposal'}</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/assets/disposals')}>
            <ArrowLeft size={16} />
            Back
          </button>
          {!isViewMode && (
            <>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleReset}>
                <RotateCcw size={16} />
                Reset
              </button>
              <button type="button" className="btn-primary" onClick={handleSave} disabled={isLoading}>
                <Save size={16} />
                {isLoading ? 'Saving...' : 'Save'}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Disposal Entry</h2>
        </div>
        <form className="form-content">
          <div className="section-header">
            <h3>Disposal Details</h3>
          </div>
          <div style={sectionStyle}>
            <div style={gridStyle}>
              <div>
                <label style={labelStyle}>
                  Disposal Code <span style={requiredStyle}>*</span>
                </label>
                <input
                  type="text"
                  name="disposalCode"
                  value={formData.disposalCode}
                  onChange={handleInputChange}
                  disabled={isViewMode || isEditMode || isLoadingCode}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  Asset <span style={requiredStyle}>*</span>
                </label>
                <select
                  name="acquisition"
                  value={formData.acquisition}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                >
                  <option value="">Select Asset</option>
                  {acquisitions.map(item => (
                    <option key={item._id} value={item._id}>
                      {item.assetName} ({item.acquisitionCode})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Disposal Date</label>
                <input
                  type="date"
                  name="disposalDate"
                  value={formData.disposalDate}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Disposal Reason</label>
                <select
                  name="disposalReason"
                  value={formData.disposalReason}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                >
                  {reasonOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Value &amp; Status</h3>
            </div>
          </div>
          <div style={sectionStyle}>
            <div style={gridStyle}>
              <div>
                <label style={labelStyle}>Disposal Value</label>
                <input
                  type="number"
                  name="disposalValue"
                  value={formData.disposalValue}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  min="0"
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Currency</label>
                <select
                  name="currency"
                  value={formData.currency}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                >
                  {currencyOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                >
                  {statusOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Additional Information</h3>
            </div>
          </div>
          <div style={sectionStyle}>
            <div style={fullWidthStyle}>
              <label style={labelStyle}>Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                disabled={isViewMode}
                rows={4}
                style={textareaStyle}
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AssetDisposalForm
